import React, { useEffect, useState } from 'react';
import { Box, Stack, Button, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ContainerPainel from 'components/container_painel';
import { getFormularioSimByUUID } from 'api/forms/pacote_formularios.api';
import { getListUsersByFilters } from 'api/user/user.api';
import { fetchAndSetUser } from './fetchUser.helper';
import Step1SimView from './steps/step1_view';
import Step2SimView from './steps/step2_view';
import Step3SimView from './steps/step3_view';
import Step4SimView from './steps/step4_view';
import Step5SimView from './steps/step5_view';
import { StepsVisibleForm } from 'components/forms/steps-visible-form';
import { ROUTES } from 'routes/constants';

// Step 1: SIM (Serviço de Inspeção Municipal)
const emptyStep1 = {
  referenceYear: new Date().getFullYear() - 1,
  hasSim: null,
  simActive: null,
  simEstablishments: 0,
  hasVet: null,
  joinedSusaf: null,
  wantsSusaf: null,
};

// Step 5: Certificação/Identificação
const emptyStep5Certificacao = {
  hasCertification: null,
  certificationSoldInFairs: null,
  hasMunicipalSeal: null,
  municipalSealName: '',
  wantsMunicipalSeal: null,
  knowsHowToCreateSeal: null,
  referenceYear: new Date().getFullYear() - 1,
};

// Step 2: Feiras da Agricultura Familiar (use emptyStep3 para Step2)
const emptyStep2 = {
  referenceYear: new Date().getFullYear() - 1,
  hasFamilyFarmersMarket: null,
  numberOfFamilyFarmers: 0,
  annualMarketValue: 0,
  totalFamilyFarmingValue: 0,
  numberOfPermanentMarkets: 0,
  numberOfNonPermanentMarkets: 0,
};

// Step 3: Feiras Permanentes
const emptyStep3 = {
  referenceYear: new Date().getFullYear() - 1,
  permanentMarkets: [],
};

// Step 4: Feiras Livres (renomeado para evitar conflito)
const emptyStep5FeirasNaoPermanentes = {
  referenceYear: new Date().getFullYear() - 1,
  nonPermanentMarkets: [],
};

// Step 6: Informações do Informante
const emptyStep6 = {
  responsibleName: '',
  responsibleRole: '',
  responsibleContact: '',
};

export const FormularioSIMVisualizar: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [step1Data, setStep1Data] = useState(emptyStep1);
  const [step2Data, setStep2Data] = useState(emptyStep2);
  const [step3Data, setStep3Data] = useState(emptyStep3); // Corrigido aqui!
  const [step4Data, setStep4Data] = useState(emptyStep5FeirasNaoPermanentes);
  const [step5Data, setStep5Data] = useState(emptyStep5Certificacao);
  const [step6Data, setStep6Data] = useState(emptyStep6);
  const [createdBy, setCreatedBy] = useState<string | null>(null);

  useEffect(() => {
    if (uuid) {
      fetchSimFormData(uuid).then((formData) => {
        setStep1Data(formData.step1);
        setStep2Data(formData.step2);
        setStep3Data(formData.step3);
        setStep4Data(formData.step4);
        setStep5Data(formData.step5);
        setStep6Data(formData.step6);
        setCreatedBy(formData.created_by || null);
      });
    }
  }, [uuid]);

  useEffect(() => {
    fetchAndSetUser(createdBy, setStep6Data);
  }, [createdBy]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: return <Step1SimView data={step1Data} />;
      case 2: return <Step2SimView data={step2Data} />;
      case 3: return <Step3SimView data={step3Data} />;
      case 4: return <Step4SimView data={step4Data} />;
      case 5: return <Step5SimView data={step5Data} />;
      default: return <Step1SimView data={step1Data} />;
    }
  }

  const steps = [
    'SIM',
    'FEIRA',
    'TIPOS DE FEIRA - FEIRAS PERMANENTES',
    'TIPOS DE FEIRA - FEIRAS LIVRES',
    'CERTIFICAÇÃO/IDENTIFICAÇÃO',
  ]

  return (
    <ContainerPainel
      pagina="Formulário de Comercialização"
      showBackButton={true}
    >
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: '24px',
          lineHeight: '133%',
          letterSpacing: '0px',
          textAlign: 'center',
          verticalAlign: 'middle',
          mt: '63px',
          mb: '32px',
        }}
      >
        Visualizar Formulário de Comercialização
      </Typography>

      <Stack marginX={'4rem'} paddingTop={'2rem'}>
        <StepsVisibleForm
          activeStep={currentStep}
          steps={steps}
          onStepChange={setCurrentStep}
        />

        <Box mt={2}>{renderStepContent()}</Box>

        <Box display="flex" justifyContent="center" mt={4} mb={2}>
          <Button
            variant="text"
            onClick={() => navigate(ROUTES.PACOTE_FORMULARIOS)}
            sx={{ fontWeight: 600, letterSpacing: '0.5px' }}
          >
            VOLTAR
          </Button>
        </Box>
      </Stack>
    </ContainerPainel>
  );
};

async function fetchSimFormData(uuid: string) {
  const data = await getFormularioSimByUUID(uuid);

  const currentYear = new Date().getFullYear();
  let referenceYear = data?.reference_year ?? currentYear - 1;

  // Garantir que o year é sempre o ano anterior ao atual
  // Isso corrige dados salvos incorretamente com o ano atual
  if (referenceYear === currentYear) {
    referenceYear = currentYear - 1;
  }

  return {
    step1: {
      referenceYear,
      hasSim: data?.has_sim ?? null,
      simActive: data?.sim_active ?? null,
      simEstablishments: data?.sim_establishments ?? 0,
      hasVet: data?.has_vet ?? null,
      joinedSusaf: data?.joined_susaf ?? null,
      wantsSusaf: data?.wants_susaf ?? null,
    },
    step2: {
      referenceYear,
      hasFamilyFarmersMarket: data?.has_family_farmers_market ?? null,
      numberOfFamilyFarmers: data?.number_of_family_farmers ?? 0,
      annualMarketValue: data?.annual_market_value ?? 0,
      totalFamilyFarmingValue: parseFloat(data?.total_family_farming_value ?? 0),
      numberOfPermanentMarkets: data?.number_of_permanent_markets ?? 0,
      numberOfNonPermanentMarkets: data?.number_of_non_permanent_markets ?? 0,
    },
    step3: {
      referenceYear,
      permanentMarkets: data?.permanent_markets ?? [],
    },
    step4: {
      referenceYear,
      nonPermanentMarkets: data?.non_permanent_markets ?? [],
    },
    step5: {
      hasCertification: data?.has_certification ?? null,
      certificationSoldInFairs: data?.certification_sold_in_fairs ?? null,
      hasMunicipalSeal: data?.has_municipal_seal ?? null,
      municipalSealName: data?.municipal_seal_name ?? '',
      wantsMunicipalSeal: data?.wants_municipal_seal ?? null,
      knowsHowToCreateSeal: data?.knows_how_to_create_seal ?? null,
      referenceYear,
    },
    step6: {
      responsibleName: '',
      responsibleRole: '',
      responsibleContact: '',
    },
    created_by: data?.created_by ?? '',
  };
}

export default FormularioSIMVisualizar;

// `fetchAndSetUser` moved to fetchUser.helper.ts
